import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import apiService from "../services/api";
import { ROUTES_NAMES } from "../components/Layout";
import { PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, resolveProgramAnalysisTaxonomyFromSearchParams } from "../constants/programAnalysisTaxonomy";
import { buildChannelsViewsRankingRows, buildChannelsInteractionsRankingRows, countChannelsFromPrograms } from "./ProgramAnalysis/top10Aggregate";
import {
  RadarPodscopeRankingCard,
  RANKING_COPY,
  RANKING_PARAM_VALUES,
  buildChannelsSocialFooterExplainer,
  formatViewsFooterExplainer,
  formatInteractionsFooterExplainer,
  formatInteractionsTotalFooterExplainer,
  formatChannelsViewsFooterExplainer,
  formatChannelsInteractionsFooterExplainer,
  formatProgramsBrandsFooterExplainer,
  formatLivestreamProgramsProgramGroupFooterExplainer,
  isExcludedLivestreamRankingChannelName,
  isExplorerLivestreamRankingParam,
  resolveLivestreamProgramRowImages,
  buildPodcastProgramImageByName,
  type ProgramRow,
  type ChannelRow,
  type RankingParam,
} from "../components/ranking-card";
import {
  downloadProgramAssetPng,
  hiddenProgramAssetCaptureShellSx,
} from "../utils/programAssetPngExport";
import {
  RANKING_ASSET_HEIGHT_PX,
  RANKING_ASSET_WIDTH_PX,
} from "../components/ranking-card/rankingAssetDimensions";
import { getLivestreamRankingDateRangeFromSearchParams } from "../utils/livestreamRankingDateRange";

const RANKING_MONTHS_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function buildRankingPeriodLabel(
  week?: string,
  startDate?: string,
  endDate?: string,
): string {
  if (week) {
    const start = new Date(week + "T12:00:00");
    if (!isNaN(start.getTime())) {
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const d1 = start.getDate();
      const d2 = end.getDate();
      const month1 = RANKING_MONTHS_ES[start.getMonth()];
      const month2 = RANKING_MONTHS_ES[end.getMonth()];
      if (month1 === month2) {
        return `Semana del ${d1} al ${d2} de ${month1}`;
      }
      return `Semana del ${d1} de ${month1} al ${d2} de ${month2}`;
    }
  }
  if (startDate && endDate) return `${startDate} – ${endDate}`;
  return "Período seleccionado";
}

const ProgramAnalysisRankingAsset: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rankingCardRef = useRef<HTMLDivElement>(null);
  const screenshotCaptureRef = useRef<HTMLDivElement>(null);
  const [isDownloadingRankingPng, setIsDownloadingRankingPng] = useState(false);

  const ranking = searchParams.get("ranking") ?? "views";
  const week = searchParams.get("week") ?? undefined;
  const podcastTaxonomy = useMemo(
    () => resolveProgramAnalysisTaxonomyFromSearchParams(searchParams),
    [searchParams],
  );
  const podcastId = searchParams.get("podcastId") ?? undefined;
  const startDate = searchParams.get("startDate") ?? undefined;
  const endDate = searchParams.get("endDate") ?? undefined;

  const validRanking = (
    RANKING_PARAM_VALUES.includes(ranking as RankingParam) ? ranking : "views"
  ) as RankingParam;

  const livestreamGroup =
    validRanking === "livestream-programs-program" ||
    validRanking === "world-cup-livestream-programs"
      ? "programs"
      : searchParams.get("livestreamGroup") === "programs"
        ? "programs"
        : "channels";

  const livestreamCategory =
    searchParams.get("livestreamCategory") === "all" ? "all" : "digital";

  const queryParams = {
    week,
    podcastTaxonomy,
    podcastId: podcastId && podcastId !== "all" ? podcastId : undefined,
    startDate,
    endDate,
  };

  const livestreamDateRange = useMemo(
    () =>
      getLivestreamRankingDateRangeFromSearchParams(week, startDate, endDate),
    [week, startDate, endDate],
  );

  const programsQuery = useQuery({
    queryKey: [
      "programs-analysis-ranking",
      { ...queryParams, ranking: validRanking },
    ],
    queryFn: () =>
      apiService.getProgramsAnalysis(
        week,
        podcastTaxonomy,
        queryParams.podcastId,
        validRanking === "views" || validRanking === "world-cup-views"
          ? "avgViewsDay7"
          : validRanking === "interactions"
            ? "avgInteractionsDay7"
            : validRanking === "interactions-total"
              ? "totalInteractionsDay7"
            : validRanking === "programs-brands"
              ? "uniquePaidBrandsCount"
              : undefined,
        undefined,
        startDate,
        endDate,
        true,
        undefined,
        validRanking === "world-cup-views",
      ),
    enabled:
      validRanking === "views" ||
      validRanking === "channels-views" ||
      validRanking === "interactions" ||
      validRanking === "interactions-total" ||
      validRanking === "channels-interactions" ||
      validRanking === "programs-brands" ||
      validRanking === "world-cup-views" ||
      validRanking === "livestream-programs-program" ||
      validRanking === "world-cup-livestream-programs",
  });

  const channelsQuery = useQuery({
    queryKey: ["channels-social-ranking", queryParams],
    queryFn: () =>
      apiService.getChannelsSocialAnalytics(
        week,
        podcastTaxonomy,
        queryParams.podcastId,
        undefined,
        startDate,
        endDate,
      ),
    enabled: validRanking === "channels-social",
  });

  const worldCupLivestreamQuery = useQuery({
    queryKey: ["ranking-world-cup-livestream-asset", livestreamDateRange],
    queryFn: () =>
      apiService.getExplorerLivestreams(
        livestreamDateRange!.startDate,
        livestreamDateRange!.endDate,
        "all",
        "all",
        {
          worldCupOnly: true,
          ranking: {
            page: 0,
            pageSize: 30,
            mode: "programs",
            sortColumn: "avgViewers",
            sortDirection: "desc",
          },
        },
      ),
    enabled:
      validRanking === "world-cup-livestream-programs" &&
      livestreamDateRange != null,
  });

  const livestreamQuery = useQuery({
    queryKey: [
      "ranking-livestream-asset",
      livestreamCategory,
      livestreamGroup,
      livestreamDateRange,
    ],
    queryFn: () =>
      apiService.getExplorerLivestreams(
        livestreamDateRange!.startDate,
        livestreamDateRange!.endDate,
        "all",
        livestreamCategory,
        livestreamGroup === "programs"
          ? {
              ranking: {
                page: 0,
                pageSize: 30,
                mode: "programs",
                sortColumn: "avgViewers",
                sortDirection: "desc",
              },
            }
          : undefined,
      ),
    enabled:
      isExplorerLivestreamRankingParam(validRanking) &&
      livestreamDateRange != null,
  });

  const podcastProgramImageByName = useMemo(
    () =>
      buildPodcastProgramImageByName(
        (programsQuery.data?.programs ?? []) as Array<{
          name: string;
          imageS3Key?: string | null;
          imageUpdatedAt?: string | null;
        }>,
      ),
    [programsQuery.data?.programs],
  );

  const isLoading =
    validRanking === "views" ||
    validRanking === "channels-views" ||
    validRanking === "interactions" ||
    validRanking === "interactions-total" ||
    validRanking === "channels-interactions" ||
    validRanking === "programs-brands" ||
    validRanking === "world-cup-views"
      ? programsQuery.isLoading
      : validRanking === "channels-social"
        ? channelsQuery.isLoading
        : validRanking === "world-cup-livestream-programs"
          ? worldCupLivestreamQuery.isLoading ||
            programsQuery.isLoading
          : isExplorerLivestreamRankingParam(validRanking)
            ? livestreamQuery.isLoading ||
              (validRanking === "livestream-programs-program"
                ? programsQuery.isLoading
                : false)
            : false;

  const activeLivestreamQuery =
    validRanking === "world-cup-livestream-programs"
      ? worldCupLivestreamQuery
      : livestreamQuery;

  const error =
    programsQuery.error ??
    channelsQuery.error ??
    activeLivestreamQuery.error;

  const programs = useMemo((): ProgramRow[] => {
    if (
      validRanking !== "views" &&
      validRanking !== "interactions" &&
      validRanking !== "interactions-total" &&
      validRanking !== "programs-brands" &&
      validRanking !== "world-cup-views"
    )
      return [];
    const list = (programsQuery.data?.programs ?? []) as any[];
    const sortKey =
      validRanking === "views" || validRanking === "world-cup-views"
        ? "avgViewsDay7"
        : validRanking === "interactions"
          ? "avgInteractionsDay7"
          : validRanking === "interactions-total"
            ? "totalInteractionsDay7"
          : "uniquePaidBrandsCount";
    return [...list]
      .sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0))
      .slice(0, 10)
      .map((p) => ({
        _id: p._id ?? p.programId,
        name: p.name,
        imageS3Key: p.imageS3Key,
        imageUpdatedAt: p.imageUpdatedAt,
        updatedAt: p.updatedAt,
        podcast: p.podcast ?? { _id: "", title: "" },
        avgViews: p.avgViews ?? 0,
        avgViewsDay7:
          typeof p.avgViewsDay7 === "number" ? p.avgViewsDay7 : undefined,
        avgInteractions: p.avgInteractions,
        avgInteractionsDay7:
          typeof p.avgInteractionsDay7 === "number"
            ? p.avgInteractionsDay7
            : undefined,
        totalInteractionsDay7:
          typeof p.totalInteractionsDay7 === "number"
            ? p.totalInteractionsDay7
            : undefined,
        uniquePaidBrandsCount: p.uniquePaidBrandsCount,
      }));
  }, [programsQuery.data, validRanking]);

  const channelsSocial = useMemo((): ChannelRow[] => {
    if (validRanking !== "channels-social") return [];
    return (channelsQuery.data?.channels ?? []).map((c: any) => ({
      _id: c._id,
      name: c.name,
      imageS3Key: c.imageS3Key,
      imageUrl: c.imageUrl,
      imageUpdatedAt: c.imageUpdatedAt,
      updatedAt: c.updatedAt,
      totalViews: c.totalViews,
      contentCount: c.contentCount,
    }));
  }, [channelsQuery.data, validRanking]);

  const channelsLivestream = useMemo((): ChannelRow[] => {
    if (
      !isExplorerLivestreamRankingParam(validRanking) &&
      validRanking !== "world-cup-livestream-programs"
    ) {
      return [];
    }
    const data = activeLivestreamQuery.data;
    if (!data) return [];

    if (livestreamGroup === "programs") {
      const imageByChannelId = new Map(
        (data.channels ?? []).map((c: { _id: string; imageS3Key?: string }) => [
          String(c._id),
          c.imageS3Key,
        ] as const),
      );
      const programRows = data.ranking?.programRows ?? [];
      return programRows
        .filter(
          (row) =>
            !isExcludedLivestreamRankingChannelName(row.sampleChannelName),
        )
        .slice(0, 10)
        .map((row) => {
          const images = resolveLivestreamProgramRowImages(
            row,
            podcastProgramImageByName,
            imageByChannelId,
          );
          // Main thumb: program cover, else channel image as fallback.
          const mainImage =
            images.programImage ??
            (images.channelImageS3Key
              ? { imageS3Key: images.channelImageS3Key }
              : undefined);
          return {
            _id: row.key,
            name: row.programName,
            imageS3Key: mainImage?.imageS3Key,
            imageUpdatedAt: images.programImage?.imageUpdatedAt,
            // Channel badge even when it matches the program cover.
            channelImageS3Key: images.channelImageS3Key,
            avgLiveViewers: row.metrics?.avgViewers ?? 0,
            peakLiveViewers: row.metrics?.maxPeakViewers ?? 0,
            avgUniqueChatUsers: row.metrics?.avgUniqueChatUsers ?? 0,
            livestreamCount: row.episodeCount,
          };
        });
    }

    const list = (data.channels ?? []).filter(
      (c) => !isExcludedLivestreamRankingChannelName(c.channelName),
    );
    return [...list]
      .sort(
        (a, b) => (b.metrics?.avgViewers ?? 0) - (a.metrics?.avgViewers ?? 0),
      )
      .slice(0, 10)
      .map((c) => ({
        _id: String(c._id ?? c.channelId ?? c.channelName),
        name: c.channelName,
        imageS3Key: c.imageS3Key,
        avgLiveViewers: c.metrics?.avgViewers ?? 0,
        peakLiveViewers: c.metrics?.maxPeakViewers ?? 0,
        avgUniqueChatUsers: c.metrics?.avgUniqueChatUsers ?? 0,
        livestreamCount: c.episodeCount ?? c.livestreams?.length ?? 0,
      }));
  }, [activeLivestreamQuery.data, validRanking, livestreamGroup, podcastProgramImageByName]);

  const channelsViews = useMemo((): ChannelRow[] => {
    if (validRanking !== "channels-views") return [];
    return buildChannelsViewsRankingRows(
      (programsQuery.data?.programs ?? []) as Parameters<
        typeof buildChannelsViewsRankingRows
      >[0],
    );
  }, [programsQuery.data, validRanking]);

  const channelsInteractions = useMemo((): ChannelRow[] => {
    if (validRanking !== "channels-interactions") return [];
    return buildChannelsInteractionsRankingRows(
      (programsQuery.data?.programs ?? []) as Parameters<
        typeof buildChannelsInteractionsRankingRows
      >[0],
    );
  }, [programsQuery.data, validRanking]);

  const channelsForCard =
    validRanking === "channels-views"
      ? channelsViews
      : validRanking === "channels-interactions"
        ? channelsInteractions
      : validRanking === "channels-social"
        ? channelsSocial
        : isExplorerLivestreamRankingParam(validRanking) ||
            validRanking === "world-cup-livestream-programs"
          ? channelsLivestream
          : undefined;

  const channelsMetric =
    isExplorerLivestreamRankingParam(validRanking) ||
    validRanking === "world-cup-livestream-programs"
      ? "livestream-avg"
      : "social-views";

  const rankingPeriodLabel = useMemo(
    () => buildRankingPeriodLabel(week, startDate, endDate),
    [week, startDate, endDate],
  );

  const copyKey =
    validRanking === "views"
      ? "views"
      : validRanking === "channels-views"
        ? "channelsViews"
      : validRanking === "world-cup-views"
        ? "worldCupViews"
      : validRanking === "interactions"
        ? "interactions"
        : validRanking === "interactions-total"
          ? "interactionsTotal"
        : validRanking === "channels-interactions"
          ? "channelsInteractions"
        : validRanking === "channels-social"
          ? "channelsSocial"
          : validRanking === "programs-brands"
            ? "programsBrands"
            : validRanking === "world-cup-livestream-programs"
              ? "worldCupLivestreamPrograms"
            : "livestreamPrograms";
  const copy =
    validRanking === "livestream-programs-program" ||
    validRanking === "world-cup-livestream-programs" ||
    (validRanking === "livestream-programs" && livestreamGroup === "programs")
      ? validRanking === "world-cup-livestream-programs"
        ? RANKING_COPY.worldCupLivestreamPrograms
        : RANKING_COPY.livestreamProgramsProgramGroup
      : RANKING_COPY[copyKey];

  const isLivestreamProgramGroupRanking =
    validRanking === "livestream-programs-program" ||
    (validRanking === "livestream-programs" && livestreamGroup === "programs");
  const livestreamProgramGroupCount =
    activeLivestreamQuery.data?.ranking?.total ?? 0;

  const hasData = programs.length > 0 || (channelsForCard?.length ?? 0) > 0;

  const handleDownloadRankingPng = async () => {
    setIsDownloadingRankingPng(true);
    try {
      await downloadProgramAssetPng(
        screenshotCaptureRef.current,
        `ranking-podscope-${validRanking}-${rankingPeriodLabel.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "")}.png`,
        {
          width: RANKING_ASSET_WIDTH_PX,
          height: RANKING_ASSET_HEIGHT_PX,
        },
      );
    } catch (err) {
      console.error("Ranking asset PNG download failed:", err);
    } finally {
      setIsDownloadingRankingPng(false);
    }
  };

  const handleBack = () => {
    navigate(ROUTES_NAMES.PROGRAM_ANALYSIS);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Volver a Program Analysis
        </Button>
        <Alert severity="error">Error al cargar los datos</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Volver a Program Analysis
        </Button>
        {hasData && process.env.NODE_ENV === "development" && (
          <Button
            variant="outlined"
            startIcon={
              isDownloadingRankingPng ? (
                <CircularProgress size={18} />
              ) : (
                <ImageIcon />
              )
            }
            onClick={handleDownloadRankingPng}
            disabled={isDownloadingRankingPng}
          >
            {isDownloadingRankingPng ? "Generando…" : "Download PNG"}
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !hasData ? (
        <Typography color="text.secondary">
          No hay datos para el ranking y período seleccionados.
        </Typography>
      ) : (
        <RadarPodscopeRankingCard
          ref={rankingCardRef}
          programs={
            validRanking === "views" ||
            validRanking === "interactions" ||
            validRanking === "interactions-total" ||
            validRanking === "programs-brands" ||
            validRanking === "world-cup-views"
              ? programs
              : undefined
          }
          channels={channelsForCard}
          rankingPeriodLabel={rankingPeriodLabel}
          subheader={copy.subheader}
          footerExplainer={
            validRanking === "channels-social"
              ? buildChannelsSocialFooterExplainer(week, startDate, endDate)
              : validRanking === "channels-views"
                ? formatChannelsViewsFooterExplainer(
                    countChannelsFromPrograms(
                      (programsQuery.data?.programs ?? []) as Parameters<
                        typeof countChannelsFromPrograms
                      >[0],
                    ),
                  )
              : validRanking === "channels-interactions"
                ? formatChannelsInteractionsFooterExplainer(
                    countChannelsFromPrograms(
                      (programsQuery.data?.programs ?? []) as Parameters<
                        typeof countChannelsFromPrograms
                      >[0],
                    ),
                  )
              : validRanking === "interactions"
                ? formatInteractionsFooterExplainer(
                    (programsQuery.data?.programs ?? []).length,
                  )
                : validRanking === "interactions-total"
                  ? formatInteractionsTotalFooterExplainer(
                      (programsQuery.data?.programs ?? []).length,
                    )
                : validRanking === "views"
                  ? formatViewsFooterExplainer(
                      (programsQuery.data?.programs ?? []).length,
                    )
                : validRanking === "programs-brands"
                  ? formatProgramsBrandsFooterExplainer(
                      programsQuery.data?.totalUniqueBrands ?? 0,
                    )
                  : isLivestreamProgramGroupRanking
                    ? formatLivestreamProgramsProgramGroupFooterExplainer(
                        livestreamProgramGroupCount,
                      )
                    : copy.footerExplainer
          }
          programRankingMetric={
            validRanking === "views" || validRanking === "world-cup-views"
              ? "views"
              : validRanking === "interactions"
                ? "interactions"
                : validRanking === "interactions-total"
                  ? "interactions-total"
                : validRanking === "programs-brands"
                  ? "programs-brands"
                  : undefined
          }
          channelsMetric={channelsForCard ? channelsMetric : undefined}
          rankingParam={validRanking}
          variant="page"
          livestreamTableNameColumnHeader={
            validRanking === "livestream-programs-program" ||
            validRanking === "world-cup-livestream-programs" ||
            (validRanking === "livestream-programs" &&
              livestreamGroup === "programs")
              ? "Programa"
              : "Canal"
          }
        />
      )}

      {hasData && (
        <Box ref={screenshotCaptureRef} sx={hiddenProgramAssetCaptureShellSx}>
          <RadarPodscopeRankingCard
            programs={
              validRanking === "views" ||
              validRanking === "interactions" ||
              validRanking === "interactions-total" ||
              validRanking === "programs-brands" ||
              validRanking === "world-cup-views"
                ? programs
                : undefined
            }
            channels={channelsForCard}
            rankingPeriodLabel={rankingPeriodLabel}
            subheader={copy.subheader}
            footerExplainer={
              validRanking === "channels-social"
                ? buildChannelsSocialFooterExplainer(week, startDate, endDate)
                : validRanking === "channels-views"
                  ? formatChannelsViewsFooterExplainer(
                      countChannelsFromPrograms(
                        (programsQuery.data?.programs ?? []) as Parameters<
                          typeof countChannelsFromPrograms
                        >[0],
                      ),
                    )
                : validRanking === "channels-interactions"
                  ? formatChannelsInteractionsFooterExplainer(
                      countChannelsFromPrograms(
                        (programsQuery.data?.programs ?? []) as Parameters<
                          typeof countChannelsFromPrograms
                        >[0],
                      ),
                    )
                : validRanking === "interactions"
                  ? formatInteractionsFooterExplainer(
                      (programsQuery.data?.programs ?? []).length,
                    )
                  : validRanking === "interactions-total"
                    ? formatInteractionsTotalFooterExplainer(
                        (programsQuery.data?.programs ?? []).length,
                      )
                  : validRanking === "views"
                    ? formatViewsFooterExplainer(
                        (programsQuery.data?.programs ?? []).length,
                      )
                  : validRanking === "programs-brands"
                    ? formatProgramsBrandsFooterExplainer(
                        programsQuery.data?.totalUniqueBrands ?? 0,
                      )
                    : isLivestreamProgramGroupRanking
                      ? formatLivestreamProgramsProgramGroupFooterExplainer(
                          livestreamProgramGroupCount,
                        )
                      : copy.footerExplainer
            }
            programRankingMetric={
              validRanking === "views" || validRanking === "world-cup-views"
                ? "views"
                : validRanking === "interactions"
                  ? "interactions"
                  : validRanking === "interactions-total"
                    ? "interactions-total"
                  : validRanking === "programs-brands"
                    ? "programs-brands"
                    : undefined
            }
            channelsMetric={channelsForCard ? channelsMetric : undefined}
            rankingParam={validRanking}
            variant="screenshot"
            livestreamTableNameColumnHeader={
              validRanking === "livestream-programs-program" ||
              validRanking === "world-cup-livestream-programs" ||
              (validRanking === "livestream-programs" &&
                livestreamGroup === "programs")
                ? "Programa"
                : "Canal"
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default ProgramAnalysisRankingAsset;
